/**
 * HOLD sticky buy bar.
 *
 * Mirrors the real submit button rather than owning a form of its own, so
 * there is one add-to-cart path on the page and the bar cannot disagree with
 * the button it is standing in for. The reasoning for the component is in
 * snippets/hold-sticky-buy.liquid; this file is the mechanism.
 */
(() => {
  const bar = document.querySelector('[data-hold-sticky-buy]');
  if (!bar) return;

  const proxy = bar.querySelector('[data-hold-sticky-buy-button]');
  /**
   * Re-queried on every read, never cached. Dawn replaces the whole buy
   * column when a variant changes, which detaches the button this bar is
   * standing in for - a cached reference would go on reporting the state of
   * an element that is no longer on the page.
   */
  const realButton = () => document.querySelector('.product-form__submit');
  const real = realButton();
  if (!proxy || !real) return;

  /**
   * Published so the hesitation card can lift itself clear. Read back from
   * the element rather than hard-coded: the bar's height depends on the
   * button's line count and on the safe-area inset, neither of which is
   * known at author time.
   */
  const publishHeight = () => {
    const height = bar.hidden ? 0 : bar.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--hold-sticky-buy-h', `${Math.round(height)}px`);
  };

  /**
   * Dawn rebuilds the buy column on every variant change, replacing the
   * submit button's label and toggling disabled in place. The bar reads both
   * each time rather than caching, or it ends up offering Add to cart for a
   * variant that cannot be bought.
   */
  const syncLabel = () => {
    const button = realButton();
    if (!button) return;
    const text = button.querySelector('span')?.textContent?.trim() || button.textContent.trim();
    if (text) proxy.textContent = text;
  };

  let realInView = false;
  let scrolledIn = false;

  const update = () => {
    // Disabled means sold out or unavailable in this market. A permanently
    // greyed strip following somebody down the page helps nobody.
    const button = realButton();
    const show = !!button && !button.disabled && scrolledIn && !realInView;
    if (show === !bar.hidden) {
      publishHeight();
      return;
    }
    if (show) {
      bar.hidden = false;
      requestAnimationFrame(() => {
        bar.classList.add('hold-sticky-buy--in');
        publishHeight();
      });
    } else {
      bar.classList.remove('hold-sticky-buy--in');
      bar.hidden = true;
      publishHeight();
    }
  };

  new IntersectionObserver(
    (entries) => {
      realInView = entries[entries.length - 1].isIntersecting;
      update();
    },
    { threshold: 0 }
  ).observe(real);

  /**
   * Not shown at the very top. Somebody who has not scrolled at all is
   * looking at the first photograph, and a buy bar over it before they have
   * read the name of the thing is the behaviour people block ads for. One
   * viewport-third of movement is enough to mean they are reading.
   */
  const onScroll = () => {
    const next = window.scrollY > window.innerHeight / 3;
    if (next === scrolledIn) return;
    scrolledIn = next;
    update();
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  proxy.addEventListener('click', () => {
    syncLabel();
    /**
     * Shopify's own add_to_cart fires from the real button either way, so
     * this is a marker beside it, not a replacement: bar adds are the count
     * of hold_bar_add, and in-page adds are add_to_cart minus that. Without
     * it there is no way to answer whether the bar is worth having.
     */
    if (typeof window.holdTrack === 'function') window.holdTrack('hold_bar_add');
    realButton()?.click();
  });

  // The buy column is replaced wholesale on variant change, so watch the
  // button's own attributes and re-read the label after each one.
  new MutationObserver(() => {
    syncLabel();
    update();
  }).observe(real, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'], childList: true, subtree: true });

  window.addEventListener('resize', publishHeight);
  syncLabel();
})();
